// src/services/openAiService.js

export const generateRequirementsSummary = async (data) => {
    const { 
        features = [], 
        responses = {}, 
        organization,
        questions = [],
        orgResponses = {},
        userResponses = []
    } = data;
    
    try {
        if (!features.length) {
            console.log('No features available');
            return 'No features available for summary generation.';
        }

        // Analyze questionnaire responses
        const questionnaireAnalysis = analyzeQuestionnaireResponses(questions, orgResponses, userResponses);
        
        // Analyze features
        const featureAnalysis = analyzeFeatures(features, responses);

        // Build a structured prompt for better AI output
        const prompt = `Please write a comprehensive executive summary of the following software requirements for ${organization?.name || 'the organization'}.

APP OVERVIEW
Here are the key questionnaire responses:
${questionnaireAnalysis.join('\n')}

REQUIREMENTS BREAKDOWN
Required Features (${featureAnalysis.required.length}):
${featureAnalysis.required.map(f => `- ${f.name}: ${f.description}`).join('\n')}

Dependencies for Required Features:
${featureAnalysis.dependencies.join('\n')}

Nice to Have Features (${featureAnalysis.niceToHave.length}):
${featureAnalysis.niceToHave.map(f => `- ${f.name}`).join('\n')}

Not Needed Features (${featureAnalysis.notNeeded.length}):
${featureAnalysis.notNeeded.map(f => `- ${f.name}`).join('\n')}

Areas Needing Clarification:
${featureAnalysis.needsClarification.join('\n')}

Please write a detailed executive summary that includes:
1. A two-sentence overview of the application based on the questionnaire responses
2. Analysis of key differences between official responses and team member responses in the questionnaire
3. Summary of required features and their dependencies 
4. Brief overview of nice-to-have features
5. List of areas needing clarification or further discussion
6. Recommendations for next steps

Keep the tone professional and focus on business objectives.`;

        console.log('Sending request to OpenAI API');

        const response = await fetch('/api/openai/summary', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ prompt })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            console.error('OpenAI API error:', errorData);
            throw new Error(errorData.error || 'Failed to generate summary');
        }

        const result = await response.json();
        return result.summary || 'Summary generation failed.';

    } catch (error) {
        console.error('Error generating AI summary:', error);
        return `Unable to generate AI summary at this time. Error: ${error.message}`;
    }
};

// Helper function to analyze questionnaire responses
function analyzeQuestionnaireResponses(questions, orgResponses, userResponses) {
    const analysis = [];

    questions.forEach(question => {
        const orgResponse = orgResponses[question.id];
        const teamResponses = userResponses.filter(r => r.question_id === question.id);
        
        // Skip if no responses
        if (!orgResponse && !teamResponses.length) return;

        // Check for differences in responses
        const hasDiscrepancies = teamResponses.some(response => 
            JSON.stringify(response.response_value) !== JSON.stringify(orgResponse?.response_value)
        );

        if (hasDiscrepancies) {
            const teamValues = teamResponses.map(r => ({
                user: r.user_name,
                value: formatResponseValue(r.response_value, question.question_type)
            }));

            analysis.push(
                `Question: ${question.question_text}`,
                `Official Response: ${formatResponseValue(orgResponse?.response_value, question.question_type)}`,
                `Team Responses: ${teamValues.map(v => `${v.user}: ${v.value}`).join('; ')}`,
                ''
            );
        }
    });

    return analysis;
}

// Helper function to analyze features
function analyzeFeatures(features, responses) {
    const required = [];
    const niceToHave = [];
    const notNeeded = [];
    const needsClarification = [];
    const dependencies = [];

    features.forEach(feature => {
        const response = responses[feature.reference];
        
        switch(response?.response_value) {
            case 'required':
                required.push(feature);
                // Add dependencies
                if (feature.dependencies?.length) {
                    dependencies.push(
                        `${feature.name} requires:`,
                        ...feature.dependencies.map(dep => 
                            `  - ${dep.name}${dep.is_system_dependency ? ' (System Dependency)' : ''}`
                        )
                    );
                }
                break;

            case 'nice_to_have':
                niceToHave.push(feature);
                break;

            case 'not_needed':
                notNeeded.push(feature);
                break;

            case 'dont_know':
                needsClarification.push(
                    `- ${feature.name}:`,
                    `  Description: ${feature.description}`,
                    feature.warning_message ? `  Warning: ${feature.warning_message}` : '',
                    ''
                );
                break;

            default:
                needsClarification.push(`- ${feature.name} (No Response)`);
        }
    });

    return {
        required,
        niceToHave,
        notNeeded,
        needsClarification,
        dependencies
    };
}

// Helper function to format response values
function formatResponseValue(value, type) {
    if (!value) return 'No Response';

    try {
        switch (type) {
            case 'multiple_choice':
                if (Array.isArray(value)) {
                    return value.join(', ');
                }
                if (typeof value === 'string') {
                    try {
                        const parsed = JSON.parse(value);
                        return Array.isArray(parsed) ? parsed.join(', ') : String(value);
                    } catch (e) {
                        return String(value);
                    }
                }
                return String(value);

            case 'range':
                try {
                    if (!value) return 'No Response';
                    
                    // Parse the range value if it's a string
                    let range = value;
                    if (typeof range === 'string') {
                        try {
                            range = JSON.parse(range);
                        } catch (e) {
                            console.error('Failed to parse range value:', e);
                            return 'Invalid Range Format';
                        }
                    }
                    
                    // Validate the range object
                    if (!range || typeof range !== 'object') {
                        console.warn('Invalid range value:', range);
                        return 'Invalid Range';
                    }
                    
                    // Safely access properties with default values
                    const currency = range.currency || '$';
                    const min = typeof range.min === 'number' ? range.min.toLocaleString() : '0';
                    const max = typeof range.max === 'number' ? range.max.toLocaleString() : '0';
                    
                    return `${currency}${min} - ${currency}${max}`;
                } catch (error) {
                    console.error('Error formatting range value:', error);
                    return 'Invalid Range Format';
                }

            case 'tristate_boolean':
                const valueMap = {
                    'YES': 'Yes',
                    'NO': 'No',
                    'DONT_KNOW': "Don't Know"
                };
                return valueMap[value] || String(value);

            default:
                return String(value);
        }
    } catch (error) {
        console.error('Error formatting response value:', error);
        return String(value);
    }
}